// src/products/analysis/services/commodityService.js
// Updated to use backend proxy endpoints (avoids CORS issues)
import { getAuthHeaders } from '../../../services/authService';

const API_BASE_URL = '/api/analysis';

/**
 * Helper to check if response is HTML (backend not available)
 */
const checkResponseType = (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('Backend API not available. Please ensure the backend server is running or deploy to Vercel.');
  }
};

/**
 * Map commodity symbols to Yahoo Finance symbols
 * Yahoo Finance uses futures symbols (e.g., GC=F for gold, CL=F for crude oil)
 */
const getYahooSymbol = (commoditySymbol) => {
  const symbolMap = {
    'WTI': 'CL=F',              // Crude Oil Futures (WTI)
    'BRENT': 'BZ=F',            // Brent Crude Oil Futures
    'NATURAL_GAS': 'NG=F',      // Natural Gas Futures
    'GOLD': 'GC=F',             // Gold Futures
    'SILVER': 'SI=F',           // Silver Futures
    'COPPER': 'HG=F',           // Copper Futures
    'ALUMINUM': 'ALI=F',        // Aluminum Futures
    'WHEAT': 'ZW=F',            // Wheat Futures
    'CORN': 'ZC=F',             // Corn Futures
    'COTTON': 'CT=F',           // Cotton Futures
    'SUGAR': 'SB=F',            // Sugar Futures
    'PLATINUM': 'PL=F',         // Platinum Futures
    'PALLADIUM': 'PA=F'         // Palladium Futures
  };
  
  return symbolMap[commoditySymbol.toUpperCase()] || commoditySymbol;
};

/**
 * Get commodity data from Yahoo Finance via backend proxy
 * Yahoo Finance provides free real-time commodity futures data
 */
export const getCommodityQuote = async (commoditySymbol) => {
  const yahooSymbol = getYahooSymbol(commoditySymbol);
  
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/yahoo/chart?symbol=${encodeURIComponent(yahooSymbol)}&interval=1d&range=1mo`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await processYahooResponse(response, commoditySymbol, yahooSymbol);
  } catch (error) {
    console.error(`Yahoo Finance error for ${commoditySymbol}:`, error);
    throw error;
  }
};

const processYahooResponse = async (response, commoditySymbol, yahooSymbol) => {
  try {
    const responseData = await response.json();
    
    // Check for errors in response
    if (!responseData || !responseData.chart || !responseData.chart.result || responseData.chart.result.length === 0) {
      throw new Error(`No data available for ${commoditySymbol}`);
    }
    
    const result = responseData.chart.result[0];
    
    // Check if result is valid
    if (!result.meta) {
      throw new Error(`Invalid data format for ${commoditySymbol}`);
    }
    
    const meta = result.meta;
    const timestamps = result.timestamp || [];
    const closes = result.indicators?.quote?.[0]?.close || [];
    const opens = result.indicators?.quote?.[0]?.open || [];
    const highs = result.indicators?.quote?.[0]?.high || [];
    const lows = result.indicators?.quote?.[0]?.low || [];
    const volumes = result.indicators?.quote?.[0]?.volume || [];
    
    // Get current price (latest close or regularMarketPrice)
    const currentPrice = meta.regularMarketPrice || 
                        (closes.length > 0 ? closes[closes.length - 1] : null) ||
                        meta.previousClose ||
                        null;
    
    // Get previous close
    const previousClose = meta.previousClose || 
                         (closes.length > 1 ? closes[closes.length - 2] : currentPrice);
    
    // Calculate price change
    const priceChange = currentPrice && previousClose ? currentPrice - previousClose : null;
    const priceChangePercent = currentPrice && previousClose && previousClose !== 0
      ? ((priceChange / previousClose) * 100)
      : null;
    
    // Get 24h high/low
    const high24h = highs.length > 0 ? Math.max(...highs.slice(-1)) : null;
    const low24h = lows.length > 0 ? Math.min(...lows.slice(-1)) : null;
    
    // Format historical data for charts
    const historicalData = timestamps.map((timestamp, index) => ({
      date: new Date(timestamp * 1000).toISOString(),
      timestamp: timestamp * 1000,
      open: opens[index] || null,
      high: highs[index] || null,
      low: lows[index] || null,
      close: closes[index] || null,
      volume: volumes[index] || null,
      value: closes[index] || null // For compatibility
    })).filter(item => item.close !== null && item.close !== undefined);
    
    return {
      currentPrice,
      previousClose,
      priceChange,
      priceChangePercent,
      high24h,
      low24h,
      volume24h: volumes.length > 0 ? volumes[volumes.length - 1] : null,
      date: timestamps.length > 0 ? new Date(timestamps[timestamps.length - 1] * 1000).toISOString() : null,
      data: historicalData
    };
  } catch (error) {
    console.error(`Error processing Yahoo Finance response for ${commoditySymbol}:`, error);
    throw error;
  }
};

/**
 * Get list of major commodities with Yahoo Finance symbols and metadata
 */
export const getMajorCommodities = () => {
  return [
    // Energy
    { symbol: 'WTI', yahooSymbol: 'CL=F', name: 'WTI Crude Oil', unit: 'USD/barrel', category: 'Energy' },
    { symbol: 'BRENT', yahooSymbol: 'BZ=F', name: 'Brent Crude Oil', unit: 'USD/barrel', category: 'Energy' },
    { symbol: 'NATURAL_GAS', yahooSymbol: 'NG=F', name: 'Natural Gas', unit: 'USD/MMBtu', category: 'Energy' },
    
    // Precious Metals
    { symbol: 'GOLD', yahooSymbol: 'GC=F', name: 'Gold', unit: 'USD/troy oz', category: 'Precious Metals' },
    { symbol: 'SILVER', yahooSymbol: 'SI=F', name: 'Silver', unit: 'USD/troy oz', category: 'Precious Metals' },
    { symbol: 'PLATINUM', yahooSymbol: 'PL=F', name: 'Platinum', unit: 'USD/troy oz', category: 'Precious Metals' },
    
    // Base Metals
    { symbol: 'COPPER', yahooSymbol: 'HG=F', name: 'Copper', unit: 'USD/pound', category: 'Metals' },
    { symbol: 'ALUMINUM', yahooSymbol: 'ALI=F', name: 'Aluminum', unit: 'USD/pound', category: 'Metals' },
    
    // Agriculture
    { symbol: 'WHEAT', yahooSymbol: 'ZW=F', name: 'Wheat', unit: 'USD/bushel', category: 'Agriculture' },
    { symbol: 'CORN', yahooSymbol: 'ZC=F', name: 'Corn', unit: 'USD/bushel', category: 'Agriculture' },
    { symbol: 'COTTON', yahooSymbol: 'CT=F', name: 'Cotton', unit: 'USD/pound', category: 'Agriculture' },
    { symbol: 'SUGAR', yahooSymbol: 'SB=F', name: 'Sugar', unit: 'USD/pound', category: 'Agriculture' }
  ];
};

/**
 * Helper to return only energy-focused markets
 */
export const getEnergyMarkets = () => {
  return getMajorCommodities().filter((commodity) => commodity.category === 'Energy');
};

/**
 * Helper to return commodities excluding the energy group
 */
export const getNonEnergyCommodities = () => {
  return getMajorCommodities().filter((commodity) => commodity.category !== 'Energy');
};

/**
 * Get commodity historical data for charts
 * Returns data in format compatible with chart components
 */
export const getCommodityHistory = async (commoditySymbol, days = 90) => {
  try {
    const quoteData = await getCommodityQuote(commoditySymbol);
    
    if (!quoteData || !quoteData.data) {
      return [];
    }
    
    // Calculate cutoff date
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    // Filter and format data
    const filteredData = quoteData.data
      .filter(item => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= cutoffDate;
      })
      .map(item => ({
        date: item.date,
        timestamp: item.timestamp,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
        volume: item.volume,
        value: item.value
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    return filteredData;
  } catch (error) {
    console.error('Error getting commodity history:', error);
    throw error;
  }
};
